import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AddToHomeScreenOutlinedIcon from '@mui/icons-material/AddToHomeScreenOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import OpenInBrowserOutlinedIcon from '@mui/icons-material/OpenInBrowserOutlined';
import {
  enablePushNotifications,
  getPushCapability,
  isAndroidDevice,
  isIOSDevice,
  isSafariBrowser,
  isStandaloneApp,
  listenForForegroundMessages,
  registerAppServiceWorker,
} from '../../Services/pushNotifications';
import { useI18n } from '../../i18n/I18nProvider';

function PwaControls({ userId }) {
  const { pick } = useI18n();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [stripeOpen, setStripeOpen] = useState(false);
  const immersiveSources = useRef(new Set());
  const [installed, setInstalled] = useState(() => isStandaloneApp());
  const [capability, setCapability] = useState(() => getPushCapability());

  const ios = isIOSDevice();
  const android = isAndroidDevice();
  const safari = isSafariBrowser();

  const refreshCapability = () => {
    setInstalled(isStandaloneApp());
    setCapability(getPushCapability());
  };

  useEffect(() => {
    const stripeStateHandler = (event) => {
      const source = String(event?.detail?.source || event?.type || 'immersive');
      if (event?.detail?.open) immersiveSources.current.add(source);
      else immersiveSources.current.delete(source);
      setStripeOpen(immersiveSources.current.size > 0);
    };
    window.addEventListener('lpb:stripe-embedded-state', stripeStateHandler);
    window.addEventListener('lpb:immersive-state', stripeStateHandler);
    return () => {
      window.removeEventListener('lpb:stripe-embedded-state', stripeStateHandler);
      window.removeEventListener('lpb:immersive-state', stripeStateHandler);
      immersiveSources.current.clear();
    };
  }, []);

  useEffect(() => {
    registerAppServiceWorker().catch(() => {});

    const installHandler = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const installedHandler = () => {
      setInstallPrompt(null);
      setInstalled(true);
      setShowInstallHelp(false);
      toast.success(pick('Le Petit Boulot est installé sur cet appareil.', 'Le Petit Boulot is installed on this device.'));
    };
    const displayModeHandler = () => refreshCapability();

    window.addEventListener('beforeinstallprompt', installHandler);
    window.addEventListener('appinstalled', installedHandler);
    window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', displayModeHandler);

    const unsubscribe = listenForForegroundMessages((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || pick('Mise à jour de votre projet', 'Project update');
      const body = payload?.notification?.body || payload?.data?.body || '';
      toast.info(body ? `${title} — ${body}` : title, { autoClose: 6500 });
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', installHandler);
      window.removeEventListener('appinstalled', installedHandler);
      window.matchMedia?.('(display-mode: standalone)').removeEventListener?.('change', displayModeHandler);
      unsubscribe?.();
    };
  }, [pick]);

  useEffect(() => {
    if (!userId) return;
    const current = getPushCapability();
    setCapability(current);

    if (current.supported && current.permission === 'granted') {
      setBusy(true);
      enablePushNotifications(userId, { requestPermission: false })
        .then(() => setRegistered(true))
        .catch(() => setRegistered(false))
        .finally(() => setBusy(false));
    }
  }, [userId]);

  const notificationMode = useMemo(() => {
    if (!userId) return 'login-required';
    if (capability.reason === 'ios-install-required') return 'install-first';
    if (capability.reason === 'permission-denied' || capability.permission === 'denied') return 'blocked';
    if (!capability.supported) return 'unsupported';
    if (registered) return 'ready';
    return 'activate';
  }, [capability, registered, userId]);

  const nativeInstallAvailable = Boolean(installPrompt);
  const manualMobileInstallAvailable = (ios || android) && !installed;
  const shouldShowInstall = !installed && (nativeInstallAvailable || manualMobileInstallAvailable);
  const shouldShowNotifications = userId && notificationMode !== 'ready';

  if (dismissed || stripeOpen || (!shouldShowInstall && !shouldShowNotifications)) return null;

  const activate = async () => {
    setBusy(true);
    try {
      await enablePushNotifications(userId);
      setRegistered(true);
      refreshCapability();
      toast.success(pick('Notifications activées sur cet appareil.', 'Notifications enabled on this device.'));
    } catch (error) {
      refreshCapability();
      if (error.reason === 'ios-install-required') setShowInstallHelp(true);
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const install = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      return;
    }
    setShowInstallHelp(true);
  };

  return (
    <Panel>
      <Close type="button" aria-label={pick('Fermer', 'Close')} onClick={() => setDismissed(true)}>×</Close>

      {shouldShowInstall && (
        <Section>
          <Title><AddToHomeScreenOutlinedIcon /><div><strong>{pick('Installer Le Petit Boulot', 'Install Le Petit Boulot')}</strong><span>{pick('Accès direct, plein écran et notifications.', 'Direct access, full screen and notifications.')}</span></div></Title>
          <Primary type="button" onClick={install}>
            {nativeInstallAvailable ? pick('Installer l’application', 'Install app') : pick('Voir les étapes exactes', 'Show exact steps')}
          </Primary>

          {showInstallHelp && ios && (
            <InstallGuide>
              {!safari && (
                <GuideStep><b>1</b><OpenInBrowserOutlinedIcon /><span>{pick('Ouvrez cette page dans Safari.', 'Open this page in Safari.')}</span></GuideStep>
              )}
              <GuideStep><b>{safari ? '1' : '2'}</b><IosShareOutlinedIcon /><span>{pick('Dans Safari, touchez le bouton Partager.', 'In Safari, tap the Share button.')}</span></GuideStep>
              <GuideStep><b>{safari ? '2' : '3'}</b><AddToHomeScreenOutlinedIcon /><span>{pick('Faites défiler les actions et touchez « Sur l’écran d’accueil ».', 'Scroll through the actions and tap “Add to Home Screen”.')}</span></GuideStep>
              <GuideStep><b>{safari ? '3' : '4'}</b><CheckCircleOutlineOutlinedIcon /><span>{pick('Activez « Ouvrir comme app web », puis touchez « Ajouter ».', 'Turn on “Open as Web App”, then tap “Add”.')}</span></GuideStep>
              <Note>{pick('Sur iPhone/iPad, Apple impose cette confirmation dans Safari : le site ne peut pas ajouter lui-même l’icône.', 'On iPhone/iPad, Apple requires this confirmation in Safari: the site cannot add the icon by itself.')}</Note>
            </InstallGuide>
          )}

          {showInstallHelp && android && (
            <InstallGuide>
              <GuideStep><b>1</b><MoreVertOutlinedIcon /><span>{pick('Dans Chrome, touchez le menu ⋮ à droite de la barre d’adresse.', 'In Chrome, tap the ⋮ menu to the right of the address bar.')}</span></GuideStep>
              <GuideStep><b>2</b><AddToHomeScreenOutlinedIcon /><span>{pick('Touchez « Ajouter à l’écran d’accueil ».', 'Tap “Add to Home screen”.')}</span></GuideStep>
              <GuideStep><b>3</b><CheckCircleOutlineOutlinedIcon /><span>{pick('Choisissez « Installer », puis suivez la confirmation affichée.', 'Choose “Install”, then follow the confirmation shown.')}</span></GuideStep>
              <Note>{pick('Si Chrome affiche directement « Installer l’application », utilisez cette option : elle installe la même web app.', 'If Chrome shows “Install app” directly, use it: it installs the same web app.')}</Note>
            </InstallGuide>
          )}
        </Section>
      )}

      {shouldShowInstall && shouldShowNotifications && <Divider />}

      {shouldShowNotifications && (
        <Section>
          <Title><NotificationsActiveOutlinedIcon /><div><strong>{pick('Notifications de projet', 'Project notifications')}</strong><span>{pick('Messages, devis, paiements et étapes importantes.', 'Messages, quotes, payments and important milestones.')}</span></div></Title>
          {notificationMode === 'blocked' ? (
            <Note>{pick('Les notifications sont bloquées. Autorisez-les dans les réglages du navigateur ou de l’appareil.', 'Notifications are blocked. Allow them in your browser or device settings.')}</Note>
          ) : notificationMode === 'unsupported' ? (
            <Note>{capability.message || pick('Les notifications ne sont pas disponibles sur cet appareil.', 'Notifications are not available on this device.')}</Note>
          ) : notificationMode === 'install-first' ? (
            <Note>{pick('Sur iPhone/iPad, installez d’abord la web app depuis Safari, puis ouvrez-la depuis son icône avant d’activer les notifications.', 'On iPhone/iPad, first install the web app from Safari, then open it from its icon before enabling notifications.')}</Note>
          ) : (
            <Secondary type="button" onClick={activate} disabled={busy}>
              {busy ? pick('Activation…', 'Enabling…') : pick('Activer les notifications', 'Enable notifications')}
            </Secondary>
          )}
        </Section>
      )}
    </Panel>
  );
}

const Panel = styled.aside`
  position:fixed;right:max(.75rem,env(safe-area-inset-right));bottom:max(.75rem,env(safe-area-inset-bottom));z-index:1600;
  width:min(25rem,calc(100vw - 1.5rem));box-sizing:border-box;padding:1rem;border:1px solid #ddd6cb;border-radius:1.1rem;background:#fff;
  box-shadow:0 1.2rem 3rem rgba(0,0,0,.2);
  @media(max-width:56rem){bottom:calc(5.8rem + env(safe-area-inset-bottom));}
`;
const Close = styled.button`position:absolute;right:.55rem;top:.45rem;width:2rem;height:2rem;border:0;border-radius:50%;background:#f2eee8;color:#111;font-size:1.25rem;cursor:pointer;`;
const Section = styled.section`display:grid;gap:.7rem;`;
const Title = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr);align-items:start;gap:.55rem;padding-right:2rem;>svg{font-size:1.3rem;color:#8a6b37;}div{display:grid;gap:.08rem;}strong{font-size:.88rem;}span{color:#706960;font-size:.7rem;line-height:1.4;}`;
const Base = styled.button`min-height:2.75rem;padding:.65rem .9rem;border-radius:999px;font:inherit;font-size:.75rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait;}`;
const Primary = styled(Base)`border:0;background:#111;color:#fff;`;
const Secondary = styled(Base)`border:1px solid #c0a375;background:#f8f3e9;color:#111;`;
const Divider = styled.hr`width:100%;height:1px;margin:.85rem 0;border:0;background:#ebe5dc;`;
const InstallGuide = styled.div`display:grid;gap:.45rem;padding:.65rem;border-radius:.8rem;background:#f7f4ef;`;
const GuideStep = styled.div`display:grid;grid-template-columns:1.3rem 1.4rem minmax(0,1fr);align-items:center;gap:.35rem;color:#4f4942;font-size:.7rem;line-height:1.4;b{width:1.3rem;height:1.3rem;display:grid;place-items:center;border-radius:50%;background:#111;color:#fff;font-size:.62rem;}svg{font-size:1.05rem;color:#8a6b37;}`;
const Note = styled.p`margin:0;color:#6f685f;font-size:.66rem;line-height:1.5;`;

export default connect(({ firebase }) => ({ userId: firebase.auth?.uid }))(PwaControls);
